<template>
  <div id="app">
    <nav :style="{ borderBottomColor: getTheme.fore }">
      <div class="nav-left">
        <a @click="setMode('traditional')" :style="traditionalTabStyles">Shield & House</a>
        <a @click="setMode('novenary')" :style="novenaryTabStyles">Novenary</a>
      </div>
      <div class="nav-right">
        <a @click="setInfo('about')" :style="{ color: getTheme.fore }">About</a>
        <a @click="copyPermalink" :style="{ color: getTheme.fore }">Permalink</a>
        <a @click="toggleTheme">{{ getThemeSymbol }}</a>
        <a @click="reset" :style="{ color: getTheme.fore }">Reset</a>
      </div>
    </nav>

    <div v-if="showAlert" :style="alertStyles">
      Permalink copied to clipboard
    </div>

    <InfoBlock v-if="blockName" />

    <div v-if="mode === 'traditional'">
      <div class="chart-title">
        <input type="text" id="querent" v-model="querent"
          :style="{ color: getTheme.fore, backgroundColor: getTheme.back }" placeholder="Optional chart title" />
      </div>
      <div class="charts">
        <Shield />
        <Houses />
      </div>
      <div class="details">
        <ShieldList />
        <HouseList />
      </div>
    </div>

    <div v-if="mode === 'novenary'">
      <input type="text" id="querent" v-model="novenaryQuerent"
        :style="{ color: getTheme.fore, backgroundColor: getTheme.back }" placeholder="Optional chart title" />
      <div class="charts">
        <Novenary />
      </div>
      <div class="details">
        <NovenaryList />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Shield from "./components/Shield.vue";
import Houses from "./components/Houses.vue";
import HouseList from "./components/HouseList.vue";
import ShieldList from "./components/ShieldList.vue";
import InfoBlock from "./components/InfoBlock.vue";
import Novenary from "./components/Novenary.vue";
import NovenaryList from "./components/NovenaryList.vue";

const tabStyles = (highlight, normalColor, highlightColor) => {
  if (highlight) {
    return {
      color: highlightColor,
      borderBottom: "3px solid " + highlightColor
    };
  } else {
    return {
      color: normalColor
    };
  }
};

export default {
  name: "app",
  components: {
    Shield,
    Houses,
    HouseList,
    ShieldList,
    InfoBlock,
    Novenary,
    NovenaryList
  },
  data() {
    return {
      showAlert: false
    };
  },
  computed: {
    ...mapGetters(["getTheme"]),
    querent: {
      get() {
        return this.$store.state.querent;
      },
      set(value) {
        this.$store.commit({ type: "setQuerent", querent: value });
      }
    },
    novenaryQuerent: {
      get() {
        return this.$store.state.novenaryQuerent;
      },
      set(value) {
        this.$store.commit({ type: "setNovenaryQuerent", querent: value });
      }
    },
    blockName() {
      return this.$store.state.blockName;
    },
    getThemeSymbol() {
      if (this.$store.state.theme === "light") {
        return "Dark Mode";
      } else {
        return "Light Mode";
      }
    },
    mode() {
      return this.$store.state.mode;
    },
    getTabUnderline() {
      return "3px solid red";
    },
    traditionalTabStyles() {
      return tabStyles(
        this.$store.state.mode === "traditional",
        this.getTheme.fore,
        this.getTheme.highlight
      );
    },
    novenaryTabStyles() {
      return tabStyles(
        this.$store.state.mode === "novenary",
        this.getTheme.fore,
        this.getTheme.highlight
      );
    },
    chartPermalink() {
      return `${window.location.protocol}//${window.location.host}${this.$store.getters.getChartUrl}`;
    },
    alertStyles() {
      return {
        position: "absolute",
        width: "14rem",
        left: "calc(50% - 7rem)",
        top: "1rem",
        border: "1px solid " + this.getTheme.fore,
        backgroundColor: this.getTheme.back,
        padding: "0.5rem",
        textAlign: "center",
        animation: "fadeOut 2s forwards",
        cursor: "default"
      };
    }
  },
  methods: {
    setInfo(label) {
      this.$store.commit("setBlockName", label);
    },
    toggleTheme() {
      if (this.$store.state.theme === "light") {
        this.$store.dispatch("setTheme", "dark");
      } else {
        this.$store.dispatch("setTheme", "light");
      }
    },
    setMode(mode) {
      this.$store.commit("changeMode", mode);
    },
    reset() {
      this.$store.commit("initChartFromCode", "0000");
      this.$store.commit("setQuerent", { querent: "" });
    },
    copyPermalink() {
      navigator.clipboard.writeText(this.chartPermalink);
      this.showAlert = true;
      setTimeout(() => {
        this.showAlert = false;
      }, 3000);
    }
  },
  beforeMount() {
    const urlParams = new URLSearchParams(window.location.search);
    this.$store.dispatch("initChartFromUrl", urlParams);
  },
  mounted() {
    document.body.style.color = this.$store.getters.getTheme.fore;
    document.body.style.backgroundColor = this.$store.getters.getTheme.back;
  }
};
</script>

<style lang="scss">
body {
  margin: 0;
  padding: 0 20px;
}

h2 {
  margin: 0;
}

label {
  font-size: 0.9rem;
  padding-right: 10px;
}

ul {
  padding-left: 0;
}

li {
  list-style: none;
  padding: 0;
  margin: 0;
}

input[type="text"] {
  border: 0;
  font-size: 1.8rem;
  font-weight: 200;
  width: 100%;
}

input[type="text"]:focus {
  outline: none;
}

#app {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  width: 850px;
  margin: 20px auto;
}

nav {
  font-size: 1.2rem;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;

  .nav-left {
    display: flex;

    a {
      margin-right: 12px;
      cursor: pointer;
    }
  }

  .nav-right {
    display: flex;
    justify-content: end;

    a {
      padding-left: 12px;
      cursor: pointer;
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }
  }
}

.chart-title {
  margin-bottom: 1.7rem;
}

.charts {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.details {
  display: flex;
  justify-content: space-between;
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }

  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    display: none;
  }
}
</style>
